import { Filter } from "@/components/organisms/MyClubSidebars/Filters/Filters.type";
import { Button } from "@/design-system/Button/Button";
import { trackFilter as trackMyClubFilter } from "@/events/myClubMixpanel";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import {
  SortBy,
  SortDirection,
  deselectPlayer,
  fetchMyClubPlayers,
  myPlayersSelector,
  removeSelectedPlayersFromClub,
  selectPlayer,
  updateFilters,
  updateIsSelecting,
} from "@repo/shared/redux";
import { useCanUseClubImportFeature } from "@repo/shared/redux";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { AddPlayersSearch } from "./components/AddPlayersSearch";
import { MyPlayersFilters } from "./components/MyPlayersFilters";
import { PlayerTable } from "./components/PlayerTable";

/**
 * My Players: every player in the club, as a table that fills the page and
 * leads with what the players are worth (price and profit). The page title
 * lives in the My Club header; filters, add and remove sit in its toolbar.
 */
export const MyClubPlayers = () => {
  const {
    players,
    totalAmountOfPlayerPages,
    filters,
    isSelecting,
    selectedPlayers,
    status,
    needsRefresh,
  } = useSelector(myPlayersSelector);
  const dispatch = useDispatch<AppDispatch>();

  const [page, setPage] = useState(1);
  // ?sort= picks the order; the side panel's two rows are two of them.
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get("sort");
  // My Players by price; Hidden Profit is the same list by profit.
  const sortFromUrl: SortBy =
    sortParam === "profit" || sortParam === "rating" ? sortParam : "price";
  const [sortBy, setSortBy] = useState<SortBy>(sortFromUrl);
  // A link here while already on the page (e.g. a card's ?sort=) still applies.
  useEffect(() => setSortBy(sortFromUrl), [sortFromUrl]);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  /*
    The players endpoint is authenticated, and a 401 force-logs the visitor out
    (see api/interceptors.ts). Nobody without a club has players to fetch, so
    the request simply doesn't go out and the table renders its empty state.
  */
  const hasClub = useCanUseClubImportFeature("see-own-players");

  /*
    One fetch per change of page, sort or filters. A new sort or filter starts
    from page 1: when the page has to move there first, that move is what
    fetches, so the old page is never requested with the new query.
  */
  const lastFilters = useRef(filters);
  useEffect(() => {
    if (!hasClub) return;
    if (lastFilters.current !== filters) {
      lastFilters.current = filters;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    dispatch(fetchMyClubPlayers({ page, sortBy, sortDirection }));
  }, [dispatch, page, sortBy, sortDirection, filters, hasClub]);

  // Players added from the search show up as soon as each one lands.
  useEffect(() => {
    if (hasClub && needsRefresh)
      dispatch(fetchMyClubPlayers({ page, sortBy, sortDirection }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRefresh]);

  const changeSort = (by: SortBy, direction: SortDirection) => {
    setSortBy(by);
    setSortDirection(direction);
    setPage(1);
    // In the URL too, so the side panel lights My Players or Hidden Profit.
    if (by !== sortBy)
      setSearchParams(
        (params) => {
          if (by === "price") params.delete("sort");
          else params.set("sort", by);
          return params;
        },
        { replace: true },
      );
  };

  const handleSelectPlayer = (player: SearchedPlayer, isSelected: boolean) => {
    // The shared slice types the rows loosely; they are this table's rows.
    const row = player as unknown as Parameters<typeof selectPlayer>[0];
    dispatch(isSelected ? deselectPlayer(row) : selectPlayer(row));
  };

  return (
    <PlayerTable
      fetchPlayersStatus={status}
      players={players}
      page={page}
      setPage={setPage}
      totalAmountOfPages={totalAmountOfPlayerPages}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={changeSort}
      isSelecting={isSelecting}
      selectedPlayers={selectedPlayers}
      handleSelectPlayer={handleSelectPlayer}
    />
  );
};

/** My Players' actions, shown in the My Club toolbar: filters, remove, add. */
export const MyClubPlayersNavigationContext = () => {
  const { t } = useTranslation(["myClub", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { filters, isSelecting, selectedPlayers } =
    useSelector(myPlayersSelector);
  const [adding, setAdding] = useState(false);

  const toggleFilter = (filter: Filter) => {
    if (Object.values(filters).every((on) => !on)) {
      trackMyClubFilter({ subsection: "My Players", filters: [filter] });
    }
    dispatch(updateFilters(filter));
  };

  return (
    // Wraps on phones, where Cancel, Remove and Add don't fit beside Filters.
    <div className="flex w-full flex-wrap items-center gap-2">
      <MyPlayersFilters filters={filters} onToggle={toggleFilter} />

      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        {isSelecting ? (
          <>
            <Button
              variant="secondary"
              size="small"
              onClick={() => dispatch(updateIsSelecting(false))}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="primary"
              size="small"
              icon={<Trash2 />}
              disabled={selectedPlayers.length === 0}
              onClick={() => dispatch(removeSelectedPlayersFromClub())}
            >
              {t("players.removeCount", { count: selectedPlayers.length })}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="small"
            icon={<Trash2 />}
            onClick={() => dispatch(updateIsSelecting(true))}
          >
            {t("players.remove")}
          </Button>
        )}
        <Button
          variant="secondary"
          size="small"
          icon={<Plus />}
          onClick={() => setAdding(true)}
        >
          {t("players.addPlayers")}
        </Button>
      </div>

      {adding && <AddPlayersSearch onClose={() => setAdding(false)} />}
    </div>
  );
};
